import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

export interface MobileNavbarContextState {
  isVisible: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const defaultState: MobileNavbarContextState = {
  isVisible: false,
  open: () => undefined,
  close: () => undefined,
  toggle: () => undefined,
};

export const MobileNavbarContext = React.createContext(defaultState);

export const useMobileNavbar = () => useContext(MobileNavbarContext);

export const MobileNavbarContextProvider = ({
  children,
}: React.PropsWithChildren) => {
  const [isVisible, setIsVisible] = useState(defaultState.isVisible);
  const location = useLocation();

  const open = useCallback(() => {
    setIsVisible(true);
  }, []);
  const close = useCallback(() => setIsVisible(false), []);
  const toggle = useCallback(() => setIsVisible((prev) => !prev), []);

  useEffect(() => {
    close();
  }, [location.pathname]);

  const state = useMemo(
    () => ({
      isVisible,
      open,
      close,
      toggle,
    }),
    [isVisible]
  );

  return (
    <MobileNavbarContext.Provider value={state}>
      {children}
    </MobileNavbarContext.Provider>
  );
};

export const withMobileNavbarContext = <P extends {}>(
  Component: React.ComponentType<P>
) => {
  return (props: P) => {
    return (
      <MobileNavbarContextProvider>
        <Component {...props} />
      </MobileNavbarContextProvider>
    );
  };
};
